/**
 * The shared meeting row (TMMAPP-0103, audit A4 §2, P:1516-1540): a 52px date badge, the title (which
 * opens the meeting editor), the IN PROGRESS pill, the when line, the virtual account pill (a prop,
 * so a virtual account's own list can hide it), the meeting link, the recurrence · place · who meta,
 * the notes excerpt and a `card` kebab. A live row is a `selected` fill with a 3px `brandBoundary`
 * rule at its start. My Meetings uses it now; the virtual account's Meetings & summaries tab
 * reuses it (TMMAPP-0108).
 *
 * Every action in the kebab is a prop: an action the viewer can't take is left out and its item
 * isn't drawn. The row never decides who may write; the page that renders it does.
 */
import { Box, Link, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { MeetingCalendarEntry } from '@/api/meetings';
import { Pill } from '@/components/Pill';
import { statusLabel } from '@/components/statusLabel';
import { describeRecurrence } from '@/features/planned/recurrence/recurrence';
import { formatCivilDateNear, formatInstant, formatZoneAbbreviation } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone, utcToZonedCivil } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { destructiveItemSx } from '@/shell/objectMenu';
import { resolveTokens } from '@/theme/theme';

export interface MeetingRowActions {
  openSession?: () => void;
  edit?: () => void;
  cancelOccurrence?: () => void;
  cancelSeries?: () => void;
  endFrom?: () => void;
  downloadIcs?: () => void;
  start?: () => void;
  end?: () => void;
}

interface MeetingRowProps {
  entry: MeetingCalendarEntry;
  /** The virtual account pill's text; leave it out to hide the pill. */
  virtualAccountName?: string | undefined;
  /** The title and the date badge open the meeting editor. */
  onOpen: () => void;
  actions: MeetingRowActions;
  /** The last row of a list card has no bottom rule. */
  last?: boolean;
}

const HTTP_URL = /^https?:\/\//i;

export function MeetingRow({
  entry,
  virtualAccountName,
  onOpen,
  actions,
  last = false,
}: MeetingRowProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const tokens = resolveTokens(useTheme());
  const zone = me.user.timezone ?? 'UTC';
  const live = entry.live;
  const meta = live ? tokens.text2 : tokens.textLabel('surface');

  const start = entry.startsAt;
  const minutes = Math.round((Date.parse(entry.endsAt) - Date.parse(start)) / 60_000);
  const month = formatInstant(start, locale, zone, { month: 'short' });
  const day = formatInstant(start, locale, zone, { day: 'numeric' });
  const when = [
    // This year's date without the year, in the viewer's zone (TMMAPP-0157 item 4).
    formatCivilDateNear(utcToZonedCivil(start, zone).date, locale, todayCivilInZone(zone)),
    `${formatInstant(start, locale, zone, { timeStyle: 'short' })} ${formatZoneAbbreviation(start, locale, zone)}`,
    minutes > 0 ? t('meetings.row.minutes', { minutes }) : null,
  ]
    .filter((bit) => bit !== null)
    .join(' · ');

  const youAreOrganizer =
    entry.organizerDisplayName !== null &&
    me.user.displayName.length > 0 &&
    entry.organizerDisplayName === me.user.displayName;
  const invited = entry.invitees
    .filter((invitee) => invitee.userId !== me.user.id)
    .map((invitee) => personName(invitee.displayName, t));
  const who = [
    youAreOrganizer
      ? t('meetings.row.youOrganizer')
      : entry.organizerDisplayName !== null
        ? t('meetings.organizer', { name: personName(entry.organizerDisplayName, t) })
        : null,
    ...invited,
  ].filter((bit): bit is string => bit !== null);
  const metaLine = [
    entry.recurrence !== null && entry.recurrence.frequency !== 'ONCE'
      ? describeRecurrence(entry.recurrence, locale, t)
      : t('meetings.oneTime'),
    entry.locationText,
    who.length > 0 ? who.join(', ') : null,
  ]
    .filter((bit): bit is string => bit !== null && bit.length > 0)
    .join(' · ');

  const badgeName = t('meetings.row.openEditor', { title: entry.title });
  const items = [
    actions.openSession !== undefined && ['openSession', actions.openSession, false],
    actions.edit !== undefined && ['edit', actions.edit, false],
    actions.cancelOccurrence !== undefined && ['cancelOccurrence', actions.cancelOccurrence, true],
    actions.cancelSeries !== undefined && ['cancelSeries', actions.cancelSeries, true],
    actions.endFrom !== undefined && ['endFrom', actions.endFrom, false],
    actions.downloadIcs !== undefined && ['downloadIcs', actions.downloadIcs, false],
    actions.start !== undefined && ['start', actions.start, false],
    actions.end !== undefined && ['end', actions.end, false],
  ].filter((item): item is [string, () => void, boolean] => item !== false);

  return (
    <Box
      data-meeting-row=""
      data-live={live ? '' : undefined}
      sx={{
        display: 'flex',
        gap: '14px',
        padding: '14px 20px',
        borderBottom: last ? 0 : `1px solid ${tokens.line}`,
        borderInlineStart: `3px solid ${live ? tokens.brandBoundary : 'transparent'}`,
        backgroundColor: live ? tokens.selected : 'transparent',
        minWidth: 0,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        aria-label={badgeName}
        sx={{
          flexShrink: 0,
          width: 52,
          height: 56,
          padding: 0,
          cursor: 'pointer',
          font: 'inherit',
          border: `1px solid ${tokens.line}`,
          borderRadius: `${tokens.radius.control}px`,
          backgroundColor: tokens.surface,
          color: tokens.text,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': { borderColor: tokens.brandBoundary },
          '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: 11,
            lineHeight: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: tokens.errorText,
          }}
        >
          {month}
        </Box>
        <Box component="span" sx={{ fontSize: 20, lineHeight: '26px', fontWeight: 400 }}>
          {day}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box
            component="button"
            type="button"
            onClick={onOpen}
            sx={{
              p: 0,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              font: 'inherit',
              fontSize: 15,
              lineHeight: '22px',
              fontWeight: 500,
              color: tokens.text,
              textAlign: 'start',
              overflowWrap: 'anywhere',
              '&:hover': { textDecoration: 'underline' },
              '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
            }}
          >
            {entry.title}
          </Box>
          {live && (
            <Pill brand small>
              {t('meetings.row.inProgress')}
            </Pill>
          )}
          {entry.status !== 'SCHEDULED' && !live && (
            <Typography component="span" variant="micro" sx={{ color: meta }}>
              {statusLabel(t, 'meeting', entry.status)}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: '2px' }}>
          {when.length > 0 && (
            <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
              {when}
            </Typography>
          )}
          {virtualAccountName !== undefined && <Pill wash="brand">{virtualAccountName}</Pill>}
          {entry.meetingUrl !== null &&
            entry.meetingUrl.length > 0 &&
            (HTTP_URL.test(entry.meetingUrl) ? (
              <Link
                href={entry.meetingUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ fontSize: 12, color: tokens.link }}
              >
                {t('meetings.row.link')}
              </Link>
            ) : (
              <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
                {entry.meetingUrl}
              </Typography>
            ))}
        </Stack>
        <Typography sx={{ fontSize: 11, lineHeight: '16px', color: meta, mt: '2px' }}>
          {metaLine}
        </Typography>
        {entry.latestNoteExcerpt !== null && entry.latestNoteExcerpt.length > 0 && (
          <Typography
            sx={{
              mt: 1,
              paddingInlineStart: '10px',
              fontSize: 12,
              lineHeight: 1.6,
              color: tokens.text2,
              borderInlineStart: `2px solid ${tokens.line}`,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {entry.latestNoteExcerpt}
          </Typography>
        )}
      </Box>

      {items.length > 0 && (
        <Box sx={{ flexShrink: 0 }}>
          <OverflowMenu
            variant="card"
            title={entry.title}
            label={t('meetings.row.actionsFor', { title: entry.title })}
          >
            {(close) =>
              items.map(([key, run, destructive]) => (
                <MenuItem
                  key={key}
                  sx={destructive ? destructiveItemSx(tokens) : undefined}
                  onClick={() => {
                    close();
                    run();
                  }}
                >
                  {t(`meetings.row.menu.${key}`)}
                </MenuItem>
              ))
            }
          </OverflowMenu>
        </Box>
      )}
    </Box>
  );
}
